import { useEffect, useState, type ReactNode } from "react";

import { loadAdminOverview, type AdminOverview } from "../../lib/admin-client";
import { AppLink } from "../../lib/navigation";
import { posthog } from "../../lib/posthog";
import { DoorwayBrandIcon } from "../public/DoorwayBrandIcon";
import { LibraryAdminPanel, type LibraryAdminPanelApi } from "./LibraryAdminPanel";

export interface AdminDashboardApi {
  loadOverview: typeof loadAdminOverview;
  library?: LibraryAdminPanelApi;
}

const defaultApi: AdminDashboardApi = { loadOverview: loadAdminOverview };
const dateTimeFormatter = new Intl.DateTimeFormat("en-US", {
  month: "short",
  day: "numeric",
  hour: "numeric",
  minute: "2-digit",
});

export function AdminDashboard({ api = defaultApi }: { api?: AdminDashboardApi }) {
  const [overview, setOverview] = useState<AdminOverview | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    void api.loadOverview().then((result) => {
      if (!active) return;
      setOverview(result);
      posthog.capture("admin_dashboard_viewed");
    }).catch((cause: unknown) => {
      if (!active) return;
      setError(cause instanceof Error ? cause.message : "Doorway could not load the admin dashboard.");
    });
    return () => { active = false; };
  }, [api]);

  if (error) {
    return (
      <AdminShell>
        <section className="admin-state-card admin-error" role="alert">
          <DoorwayBrandIcon className="admin-state-icon" name="door-outline-green" />
          <p className="eyebrow">Private owner view</p>
          <h1>Admin access unavailable</h1>
          <p>{error}</p>
          <AppLink className="secondary-button" href="/manage">Return to Doorway</AppLink>
        </section>
      </AdminShell>
    );
  }
  if (!overview) {
    return (
      <AdminShell>
        <section className="admin-state-card admin-loading" aria-live="polite">
          <div className="admin-state-orbit" aria-hidden="true"><DoorwayBrandIcon name="door-solid-green" /></div>
          <p className="eyebrow">Private owner view</p>
          <h1>Opening the control room</h1>
          <p>Gathering the latest product activity.</p>
        </section>
      </AdminShell>
    );
  }

  const metrics = [
    ["Users", overview.totals.users, "Accounts created", "people"],
    ["Workspaces", overview.totals.workspaces, "Places to build", "workspaces"],
    ["Projects", overview.totals.projects, "Draft and live", "projects"],
    ["Live apps", overview.totals.liveApps, "Published now", "live"],
    ["Paid workspaces", overview.totals.paidWorkspaces, "Active upgrades", "paid"],
  ] as const;
  const showLibrary = api === defaultApi || api.library !== undefined;

  return (
    <AdminShell>
      <section className="admin-hero" aria-labelledby="admin-heading">
        <div className="admin-hero-copy">
          <p className="eyebrow"><span className="admin-live-dot" aria-hidden="true" />Private owner view</p>
          <h1 id="admin-heading">Doorway at a glance</h1>
          <p>One calm view of the people building, the work getting published, and the plans powering it all.</p>
          <p className="admin-updated">Fresh as of <time dateTime={overview.generatedAt}>{formatDate(overview.generatedAt)}</time></p>
        </div>
        <div className="admin-pulse" aria-label="Doorway network status">
          <div className="admin-pulse-sky" aria-hidden="true">
            <span className="admin-cloud admin-cloud-one" />
            <span className="admin-cloud admin-cloud-two" />
            <span className="admin-pulse-path" />
            <DoorwayBrandIcon className="admin-pulse-door" name="door-solid-green" />
          </div>
          <div className="admin-pulse-copy">
            <span><i aria-hidden="true" />Doorway network</span>
            <strong>Open and running</strong>
          </div>
        </div>
      </section>

      <section className="admin-metrics" aria-label="Product totals">
        {metrics.map(([label, value, note, tone]) => (
          <article className={`admin-metric admin-metric-${tone}`} key={label}>
            <span className="admin-metric-mark" aria-hidden="true" />
            <strong>{value.toLocaleString()}</strong>
            <span>{label}</span>
            <small>{note}</small>
          </article>
        ))}
      </section>

      <section className="admin-plan-strip" aria-labelledby="plan-mix-heading">
        <div className="admin-plan-heading"><p className="eyebrow">Current plan mix</p><h2 id="plan-mix-heading">Workspace runway</h2></div>
        <div className="admin-plan-overview">
          <div
            className="admin-plan-track"
            role="img"
            aria-label={`${overview.plans.free} Free, ${overview.plans.solo} Solo, and ${overview.plans.team} Team workspaces`}
          >
            {overview.plans.free > 0 && <span className="admin-plan-free" style={{ flexGrow: overview.plans.free }} />}
            {overview.plans.solo > 0 && <span className="admin-plan-solo" style={{ flexGrow: overview.plans.solo }} />}
            {overview.plans.team > 0 && <span className="admin-plan-team" style={{ flexGrow: overview.plans.team }} />}
          </div>
          <div className="admin-plan-counts">
            <span><i className="admin-plan-free" aria-hidden="true" />{overview.plans.free} Free</span>
            <span><i className="admin-plan-solo" aria-hidden="true" />{overview.plans.solo} Solo</span>
            <span><i className="admin-plan-team" aria-hidden="true" />{overview.plans.team} Team</span>
          </div>
        </div>
      </section>

      <div className="admin-grid">
        <section className="admin-panel" aria-labelledby="recent-users-heading">
          <div className="admin-panel-heading"><p className="eyebrow">People</p><h2 id="recent-users-heading">Newest users</h2></div>
          {overview.recentUsers.length === 0 ? <p className="admin-empty">No users yet.</p> : (
            <ul className="admin-list">
              {overview.recentUsers.map((user) => (
                <li key={user.id}>
                  <span className="admin-list-avatar" aria-hidden="true">{user.name.charAt(0).toUpperCase() || "?"}</span>
                  <div><strong>{user.name}</strong><span>{user.email}</span></div>
                  <time dateTime={user.createdAt}>{formatDate(user.createdAt)}</time>
                </li>
              ))}
            </ul>
          )}
        </section>

        <section className="admin-panel" aria-labelledby="recent-projects-heading">
          <div className="admin-panel-heading"><p className="eyebrow">What shipped</p><h2 id="recent-projects-heading">Recent projects</h2></div>
          {overview.recentProjects.length === 0 ? <p className="admin-empty">No projects yet.</p> : (
            <ul className="admin-list admin-project-list">
              {overview.recentProjects.map((project) => (
                <li key={project.id}>
                  <span className="admin-list-project-icon" aria-hidden="true"><span /></span>
                  <div>
                    <a href={project.url} target="_blank" rel="noreferrer">{project.name}</a>
                    <span>{project.workspace} / {titleCase(project.plan)}</span>
                  </div>
                  <span className={`admin-status admin-status-${project.status}`}>{titleCase(project.status)}</span>
                </li>
              ))}
            </ul>
          )}
        </section>
      </div>
      {showLibrary && <LibraryAdminPanel {...(api.library ? { api: api.library } : {})} />}
    </AdminShell>
  );
}

function AdminShell({ children }: { children: ReactNode }) {
  return (
    <div className="admin-shell admin-oasis-shell ph-no-capture">
      <header className="site-header admin-oasis-header">
        <AppLink className="wordmark" href="/manage" aria-label="Doorway workspace">
          <DoorwayBrandIcon className="admin-oasis-logo" name="logo-tile-green" />
          Doorway
        </AppLink>
        <div className="header-actions"><AppLink className="header-link" href="/manage/workspaces">Workspace</AppLink><span className="demo-pill">Owner view</span></div>
      </header>
      <main className="admin-main">{children}</main>
    </div>
  );
}

function formatDate(value: string): string {
  return dateTimeFormatter.format(new Date(value));
}

function titleCase(value: string): string {
  return `${value.charAt(0).toUpperCase()}${value.slice(1)}`;
}
